<template>
  <div class="sv-settings" data-test="sv-settings">
    <button
      type="button"
      class="sv-settings__gear"
      :class="{ 'sv-settings__gear--open': open }"
      aria-label="Schedule View settings"
      title="Schedule View settings"
      data-test="sv-settings-gear"
      @click="toggle"
    ><i class="fa fa-cog" aria-hidden="true"></i></button>

    <!--
      Phone only (hidden by CSS elsewhere): the backdrop behind the bottom sheet. Tapping it follows
      the same rule as an outside click — it closes a clean form and leaves an edited one open, so
      a stray tap on the dimmed schedule cannot throw away somebody's changes.
    -->
    <div
      v-if="open"
      class="sv-settings__backdrop"
      aria-hidden="true"
      data-test="sv-settings-backdrop"
      @click="onBackdrop"
    ></div>

    <div v-if="open" class="sv-settings__panel" data-test="sv-settings-panel">
      <div class="sv-settings__head">
        <span>Schedule View settings</span>
        <button
          type="button"
          class="sv-settings__x"
          aria-label="Close"
          data-test="sv-settings-close"
          @click="close"
        >&times;</button>
      </div>

      <p class="sv-settings__scope" data-test="sv-settings-scope">
        These settings apply to <strong>everyone in this office</strong>.
      </p>

      <div v-if="!form" class="sv-settings__body sv-settings__loading">
        Loading this office's settings…
      </div>

      <div v-else class="sv-settings__body">
        <!-- ── Appointment Block ─────────────────────────────────────────────────────────── -->
        <h4 class="sv-settings__section">Appointment Block</h4>

        <h5 class="sv-settings__subsection">Alerts</h5>
        <label
          v-for="alert in alerts"
          :key="alert.key"
          class="sv-settings__row"
          :class="{ 'sv-settings__row--disabled': !alert.available }"
        >
          <input
            type="checkbox"
            :checked="form.alerts[alert.key]"
            :disabled="!alert.available"
            :data-test="`sv-alert-${alert.key}`"
            @change="form.alerts[alert.key] = $event.target.checked"
          />
          <i class="fa sv-settings__icon" :class="alert.icon" aria-hidden="true"></i>
          <span class="sv-settings__row-text">
            {{ alert.label }}
            <small
              v-if="!alert.available"
              class="sv-settings__hint"
              :class="{ 'sv-settings__hint--reason': alert.fromServer }"
              :data-test="`sv-alert-reason-${alert.key}`"
            >{{ alert.hint }}</small>
          </span>
        </label>

        <label class="sv-settings__money">
          <span>Only show a balance over</span>
          <span class="sv-settings__money-input">
            <span aria-hidden="true">$</span>
            <input
              type="number"
              min="0"
              step="1"
              inputmode="decimal"
              v-model="form.balanceMinimum"
              data-test="sv-balance-minimum"
            />
          </span>
        </label>

        <hr class="sv-settings__rule" />

        <label class="sv-settings__row">
          <input
            type="checkbox"
            :checked="form.showProcedures"
            data-test="sv-show-procedures"
            @change="form.showProcedures = $event.target.checked"
          />
          <span class="sv-settings__row-text">Show appointment procedures description</span>
        </label>

        <label class="sv-settings__row">
          <input
            type="checkbox"
            :checked="form.showConfirmation"
            data-test="sv-show-confirmation"
            @change="form.showConfirmation = $event.target.checked"
          />
          <span class="sv-settings__row-text">Show appointment confirmation status</span>
        </label>

        <!-- ── Schedule View ─────────────────────────────────────────────────────────────── -->
        <h4 class="sv-settings__section">Schedule View</h4>

        <label class="sv-settings__row">
          <input
            type="checkbox"
            :checked="form.showBlockouts"
            data-test="sv-show-blockouts"
            @change="form.showBlockouts = $event.target.checked"
          />
          <span class="sv-settings__row-text">Show blockouts from Open Dental</span>
        </label>

        <label class="sv-settings__row">
          <input
            type="checkbox"
            :checked="form.showBrokenAppointments"
            data-test="sv-show-broken"
            @change="form.showBrokenAppointments = $event.target.checked"
          />
          <span class="sv-settings__row-text">Show broken appointments</span>
        </label>

        <!-- ── Confirmation statuses (collapsible) ───────────────────────────────────────── -->
        <button
          type="button"
          class="sv-settings__collapse"
          :aria-expanded="statusesOpen ? 'true' : 'false'"
          data-test="sv-statuses-toggle"
          @click="statusesOpen = !statusesOpen"
        >
          <span>Confirmation statuses</span>
          <span aria-hidden="true">{{ statusesOpen ? '&minus;' : '+' }}</span>
        </button>

        <div v-if="statusesOpen" class="sv-settings__statuses" data-test="sv-statuses">
          <p class="sv-settings__note">
            Your Open Dental status names, and the colour each one shows on the schedule.
          </p>
          <p v-if="!confirmationStatuses.length" class="sv-settings__note">
            No appointment confirmation statuses are set up in Open Dental for this office.
          </p>
          <div
            v-for="def in confirmationStatuses"
            :key="def.DefNum"
            class="sv-settings__status-row"
          >
            <span class="sv-settings__status-name" :title="def.ItemName">{{ def.ItemName }}</span>
            <select
              class="sv-settings__status-select"
              :value="bucketFor(def)"
              :data-test="`sv-status-${def.DefNum}`"
              @change="setBucket(def, $event.target.value)"
            >
              <option v-for="b in buckets" :key="b" :value="b">{{ bucketLabels[b] }}</option>
            </select>
          </div>
        </div>
      </div>

      <p v-if="errorMessage" class="sv-settings__error" data-test="sv-settings-error">
        {{ errorMessage }}
      </p>

      <div class="sv-settings__foot">
        <button
          type="button"
          class="sv-settings__cancel"
          data-test="sv-settings-cancel"
          @click="close"
        >Cancel</button>
        <button
          type="button"
          class="sv-settings__save"
          :disabled="!form || saving || !dirty"
          data-test="sv-settings-save"
          @click="submit"
        >{{ saving ? 'Saving…' : 'Save' }}</button>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * The gear popover — the office's Schedule View settings.
 *
 * ── PER OFFICE, AND SAID SO ON SCREEN ────────────────────────────────────────────────────────
 * One blob for the whole practice. Somebody turning an alert off turns it off for everybody, so
 * the panel says that where they can see it before they click, not in a help article afterwards.
 *
 * ── THE DEFAULTS ARE NOT HERE ────────────────────────────────────────────────────────────────
 * The form is built from the RESOLVED settings that /schedule-view/day returned. This file never
 * decides what "absent" means — schedule-view-service/lib/settings.js does, once.
 *
 * ── GREYED-OUT ALERTS ────────────────────────────────────────────────────────────────────────
 * Four alerts need another feature configured (Forms, Form Update Reminders, Tx Presenter, Insurance
 * Verification). The server says which are available AND why not (`reasons`); an unavailable one is
 * drawn DISABLED with a small RED line naming what unlocks it ("Needs Forms feature enabled"). A
 * service that sends no reasons gets the page's own grey hint instead. Its stored value is left
 * exactly as it is, so enabling the feature later turns on the alert they already asked for.
 *
 * ── CONFIRMATION STATUSES: ONLY OVERRIDES ARE STORED ─────────────────────────────────────────
 * Each status is shown at its effective bucket — the office's override if there is one, otherwise
 * the guess from its name. Choosing the value that was already guessed stores NOTHING. That keeps
 * "nobody has touched this" distinguishable from "somebody chose exactly these", and it lets the
 * guesses keep working for statuses the office adds later.
 */
import {
  ALERT_META, BUCKET_LABELS, STATUS_BUCKETS,
  formFromSettings, isDirty, describeAlertAvailability,
} from '@/utils/scheduleView/settingsForm';
import { guessBucket } from '@/utils/scheduleView/statusBuckets';

export default {
  name: 'ScheduleSettingsPopover',

  props: {
    /** The RESOLVED settings from /schedule-view/day. Null until the first load. */
    settings: { type: Object, default: null },
    /** { forms, medhx, txsign, insverify } — which gated alerts this site can compute. */
    capabilities: { type: Object, default: () => ({}) },
    /**
     * { <alertKey>: 'NEEDS_FORMS' | 'NEEDS_FORM_REMINDERS' | 'NEEDS_TX_PRESENTER' |
     * 'NEEDS_INSURANCE_VERIFICATION' } — why each unavailable alert is. Null from a service that
     * predates the reasons.
     */
    reasons: { type: Object, default: null },
    /** The office's own OD confirmation statuses (definition rows). */
    confirmationStatuses: { type: Array, default: () => [] },
    saving: { type: Boolean, default: false },
    /** A failed save's reason, shown in place rather than as a toast over a form. */
    errorMessage: { type: String, default: '' },
  },

  data() {
    return {
      open: false,
      statusesOpen: false,
      form: null,
      buckets: STATUS_BUCKETS,
      bucketLabels: BUCKET_LABELS,
    };
  },

  computed: {
    alerts() {
      return ALERT_META.map(meta => ({
        ...meta,
        ...describeAlertAvailability(meta.key, this.capabilities, this.reasons),
      }));
    },

    dirty() {
      return isDirty(this.form, this.settings);
    },
  },

  watch: {
    /**
     * A background refresh lands every two minutes, and these settings are shared by the whole
     * office — so the value can change under an open panel because a COLLEAGUE saved.
     *
     * An untouched form follows the server; a form somebody is halfway through editing does not.
     * The comparison is against the PREVIOUS settings, not the new ones: comparing against the new
     * ones would read a colleague's change as this user's edit, keep the stale form, and then let
     * Save quietly overwrite what the colleague just did.
     */
    settings(next, previous) {
      if (this.open && !isDirty(this.form, previous)) this.form = formFromSettings(next);
    },
  },

  mounted() {
    document.addEventListener('click', this.onDocumentClick, true);
    document.addEventListener('keydown', this.onKeydown);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.onDocumentClick, true);
    document.removeEventListener('keydown', this.onKeydown);
  },

  methods: {
    toggle() {
      if (this.open) {
        this.close();
        return;
      }
      this.form = formFromSettings(this.settings);
      this.open = true;
    },

    close() {
      this.open = false;
      this.form = null;
      this.statusesOpen = false;
      this.$emit('dismiss');
    },

    /** The bucket this status effectively has: the office's override, else the guess. */
    bucketFor(def) {
      const stored = this.form && this.form.confirmationBuckets[String(def.DefNum)];
      return stored || guessBucket(def.ItemName);
    },

    setBucket(def, bucket) {
      const key = String(def.DefNum);
      if (bucket === guessBucket(def.ItemName)) {
        // Back to the guess: drop the override rather than freezing it, so the blob stays a record
        // of decisions the office actually made.
        this.$delete(this.form.confirmationBuckets, key);
        return;
      }
      this.$set(this.form.confirmationBuckets, key, bucket);
    },

    submit() {
      this.$emit('save', this.form);
    },

    onDocumentClick(event) {
      if (!this.open) return;
      if (this.$el && this.$el.contains(event.target)) return;
      // Closing on an outside click would DISCARD unsaved edits with no warning. A form somebody
      // has changed stays open until they choose Save or Cancel.
      if (this.dirty) return;
      this.close();
    },

    onKeydown(event) {
      if (event.key !== 'Escape' || !this.open) return;
      if (this.dirty) return;
      this.close();
    },

    /**
     * The backdrop is inside this component, so the outside-click handler above ignores it — it
     * needs its own handler, with the same don't-discard-edits rule.
     */
    onBackdrop() {
      if (this.dirty) return;
      this.close();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../tokens';

.sv-settings {
  position: relative;
}

// The gear: a 38px square button like the filters beside it. Open (or hovered) it takes the teal-subtle
// fill and a teal icon — the design's `.icon-btn.active`.
.sv-settings__gear {
  width: 38px;
  height: 38px;
  padding: 0;
  border: 1px solid $sv-line;
  border-radius: $sv-radius;
  background: $sv-surface;
  box-shadow: $sv-shadow-1;
  color: $sv-ink-2;
  font-size: 15px;
  cursor: pointer;

  &:hover { background: $sv-bg; border-color: $sv-line-strong; }
  &:focus { outline: none; }
  &:focus-visible { outline: 2px solid $sv-brand-ring; outline-offset: 2px; }
  &--open,
  &--open:hover { background: $sv-brand-subtle; border-color: $sv-brand; color: $sv-brand-deep; }
}

.sv-settings__panel {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 40;
  width: 340px;
  max-width: calc(100vw - 24px);
  background: $sv-surface;
  border: 1px solid $sv-line;
  border-radius: $sv-radius-lg;
  box-shadow: $sv-shadow-3;
  display: flex;
  flex-direction: column;
  max-height: min(70vh, 560px);
}

.sv-settings__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid $sv-line;
  font-size: 13px;
  font-weight: 700;
  color: $sv-ink;
}

.sv-settings__x {
  border: 0;
  background: none;
  padding: 0 2px;
  font-size: 20px;
  line-height: 1;
  color: $sv-ink-3;
  cursor: pointer;
}

.sv-settings__scope {
  margin: 0;
  padding: 7px 12px;
  background: $sv-bg;
  border-bottom: 1px solid $sv-line;
  font-size: 11.5px;
  color: $sv-ink-3;
}

.sv-settings__body {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 4px 12px 10px;
}

.sv-settings__loading {
  padding: 18px 12px;
  font-size: 13px;
  color: $sv-ink-3;
}

.sv-settings__section {
  margin: 12px 0 4px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: $sv-ink;
}

.sv-settings__subsection {
  margin: 6px 0 2px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: $sv-ink-3;
}

.sv-settings__row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0;
  // 34px keeps every row a usable tap target on the operatory iPad.
  min-height: 34px;
  padding: 6px 2px;
  font-size: 13px;
  font-weight: 500;
  color: $sv-ink;
  cursor: pointer;

  input { margin-top: 2px; flex: 0 0 auto; }

  &--disabled { color: $sv-ink-3; cursor: default; }
}

.sv-settings__icon {
  width: 15px;
  margin-top: 2px;
  flex: 0 0 auto;
  text-align: center;
  color: $sv-ink-3;
}

.sv-settings__row-text {
  min-width: 0;
}

.sv-settings__hint {
  display: block;
  margin-top: 1px;
  font-size: 11px;
  line-height: 1.35;
  color: $sv-ink-3;

  // The server's reason an alert is unavailable ("Needs Forms feature enabled"): small, red.
  &--reason { color: #a5292e; }
}

.sv-settings__money {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin: 4px 0 2px;
  padding: 2px 2px 2px 23px;
  font-size: 13px;
  font-weight: 500;
  color: $sv-ink;
}

.sv-settings__money-input {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: $sv-ink-3;

  input {
    width: 88px;
    height: 32px;
    padding: 0 6px;
    border: 1px solid $sv-line;
    border-radius: $sv-radius;
    // 16px in px, not rem: below 16px iOS Safari zooms in on focus and never zooms back out.
    font-size: 16px;
    color: $sv-ink;

    &:focus { outline: none; border-color: $sv-brand; }
  }
}

.sv-settings__rule {
  margin: 10px 0 6px;
  border: 0;
  border-top: 1px solid $sv-line;
}

.sv-settings__collapse {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: 12px;
  padding: 8px 0;
  border: 0;
  border-top: 1px solid $sv-line;
  background: none;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: $sv-ink;
  cursor: pointer;
}

.sv-settings__note {
  margin: 0 0 6px;
  font-size: 11.5px;
  line-height: 1.4;
  color: $sv-ink-3;
}

.sv-settings__status-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 0;
}

.sv-settings__status-name {
  font-size: 13px;
  color: $sv-ink;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sv-settings__status-select {
  flex: 0 0 auto;
  width: 132px;
  height: 32px;
  padding: 0 4px;
  border: 1px solid $sv-line;
  border-radius: $sv-radius;
  background: $sv-surface;
  font-size: 16px; // see the note on the money input
  color: $sv-ink;
}

.sv-settings__error {
  margin: 0;
  padding: 8px 12px;
  border-top: 1px solid $sv-line;
  background: #fdf1f1;
  font-size: 12px;
  color: #a5292e;
}

.sv-settings__foot {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 10px 12px;
  border-top: 1px solid $sv-line;
}

.sv-settings__cancel,
.sv-settings__save {
  height: 32px;
  padding: 0 14px;
  border-radius: $sv-radius;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.sv-settings__cancel {
  border: 1px solid $sv-line;
  background: $sv-surface;
  color: $sv-ink-2;
}

.sv-settings__save {
  border: 1px solid $sv-brand;
  background: $sv-brand;
  color: #ffffff;

  &:disabled { opacity: 0.5; cursor: default; }
}

.sv-settings__backdrop {
  display: none;
}

/**
 * Phone: a bottom sheet rather than a 340px card hanging off a 34px button (Phase 6).
 *
 * Full width and flush to the bottom edge, where the thumb is; the body scrolls between a fixed
 * header and a fixed Save bar, so Save is never scrolled out of reach. Same breakpoint as the
 * timeline — see $sv-phone-max.
 *
 * z-index 10000+: above the AI agent's floating button (agent-panel.css, 9999 !important). Below
 * that, the button sat on top of the sheet and covered the Confirmation statuses toggle.
 */
@media (max-width: $sv-phone-max) {
  .sv-settings__backdrop {
    display: block;
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 10000;
    background: rgba(12, 15, 14, 0.32);
  }

  .sv-settings__panel {
    position: fixed;
    top: auto;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 10001;
    width: auto;
    max-width: none;
    max-height: 85vh;
    border-bottom: 0;
    border-radius: 16px 16px 0 0;
    box-shadow: 0 -8px 30px rgba(12, 15, 14, 0.18);
  }

  .sv-settings__head { padding: 14px 16px; }

  .sv-settings__body {
    padding: 4px 16px 12px;
    // A scroll that reaches the end of the form must not carry on scrolling the schedule behind.
    overscroll-behavior: contain;
  }

  .sv-settings__row {
    min-height: 44px;
    padding: 10px 2px;
    font-size: 14.5px;

    // Top-aligned, not centred: a greyed-out alert carries a second line saying what unlocks it,
    // and the box belongs beside the alert's name, not halfway down the hint.
    input { width: 20px; height: 20px; margin-top: 0; }
  }

  .sv-settings__foot {
    padding: 10px 16px calc(10px + env(safe-area-inset-bottom));
  }

  .sv-settings__cancel,
  .sv-settings__save {
    flex: 1 1 0;
    height: 44px;
    font-size: 15px;
  }
}
</style>
